import 'date-fns';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
    StaticDateTimePicker,
    LocalizationProvider
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Button, Typography, Calendar, ClockOutline, Box } from 'components';

const DateSelectorContainer = styled.div`
    display: flex;
    overflow: hidden;
`;

const CalendarContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
`;

const CalendarHeaderContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: baseline;

    & > *:first-child {
        margin-right: 0.5rem;
    }
`;

const DateTimePickerContainer = styled.div`
    & > *:first-child {
        margin-bottom: 1rem;
    }
`;

const QuickSelectorContainer = styled.div`
    display: flex;
    flex-direction: column;
    border-left: 1px rgba(0, 0, 0, 0.2) solid;
    margin-left: 1.5rem;
    padding-left: 1rem;
    margin-top: 3rem;

    & > * {
        margin-bottom: 0.5rem;
    }
`;

const DateTimeLabel = styled.div`
    display: flex;
    width: 100%;
    line-height: 24px;
    justify-content: space-evenly;
    align-items: center;
    color: ${({ theme }) => theme.palette.common.white};
`;

export interface DateSelectorProps {
    setFilter: (updater: any) => void;
    filter: any;
}

export const DateSelector = ({ setFilter, filter }: DateSelectorProps) => {
    const { t } = useTranslation();
    const [startDate, setStartDate] = useState<Date>(() => {
        if (filter?.value?.from) {
            return filter.value.from;
        }

        const today = new Date();
        today.setDate(today.getDate() - 1);
        today.setHours(0, 0, 0, 0);
        return today;
    });
    const [endDate, setEndDate] = useState<Date>(() => {
        if (filter?.value?.to) {
            return filter.value.to;
        }

        return new Date(
            startDate.getFullYear(),
            startDate.getMonth(),
            startDate.getDate() + 1
        );
    });

    useEffect(() => {
        setFilter({
            value: {
                from: startDate,
                to: endDate
            }
        });
    }, [endDate, startDate, setFilter]);

    const selectYesterday = () => {
        const date = new Date();
        date.setDate(date.getDate() - 1);
        date.setHours(0, 0, 0, 0);
        setStartDate(new Date(date));
        date.setHours(23, 59, 59, 59);
        setEndDate(date);
    };

    const selectLastSevenDays = () => {
        const date = new Date();
        date.setDate(date.getDate() - 7);
        date.setHours(0, 0, 0, 0);
        setStartDate(new Date(date));
        date.setDate(date.getDate() + 6);
        date.setHours(23, 59, 59, 59);
        setEndDate(date);
    };

    const selectLastThirtyDays = () => {
        const date = new Date();
        date.setDate(date.getDate() - 30);
        date.setHours(0, 0, 0, 0);
        setStartDate(new Date(date));
        date.setDate(date.getDate() + 29);
        date.setHours(23, 59, 59, 59);
        setEndDate(date);
    };

    // fixme
    const selectThisMonth = () => {
        const date = new Date();
        setEndDate(new Date(date));
        date.setHours(0, 0, 0, 0);
        date.setDate(1);
        setStartDate(date);
    };

    const selectLastMonth = () => {
        const date = new Date();
        date.setHours(23, 59, 59, 59);
        date.setDate(0);
        setEndDate(new Date(date));
        date.setHours(0, 0, 0, 0);
        date.setDate(1);
        setStartDate(date);
    };

    return (
        <DateSelectorContainer>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <CalendarContainer>
                    <DateTimePickerContainer>
                        <CalendarHeaderContainer>
                            <Typography variant="subtitle1" weight="semi">
                                {t('expressions.start_date')}
                            </Typography>
                            <Typography variant="subtitle2" weight="regular">
                                {startDate.toLocaleString()}
                            </Typography>
                        </CalendarHeaderContainer>
                        <Box position="relative">
                            <StaticDateTimePicker
                                displayStaticWrapperAs="desktop"
                                dateRangeIcon={
                                    <DateTimeLabel>
                                        <span>{t('general.Date')}</span>
                                        <Calendar size={20} />
                                    </DateTimeLabel>
                                }
                                timeIcon={
                                    <DateTimeLabel>
                                        <span>{t('general.Time')}</span>
                                        <ClockOutline size={20} />
                                    </DateTimeLabel>
                                }
                                value={startDate}
                                onChange={(value: Date) => {
                                    setStartDate(value);
                                }}
                                ampm={false}
                                renderInput={() => null}
                            />
                        </Box>
                    </DateTimePickerContainer>
                    <DateTimePickerContainer>
                        <CalendarHeaderContainer>
                            <Typography variant="subtitle1" weight="semi">
                                {t('expressions.end_date')}
                            </Typography>
                            <Typography variant="subtitle2" weight="regular">
                                {endDate.toLocaleString()}
                            </Typography>
                        </CalendarHeaderContainer>
                        <Box position="relative">
                            <StaticDateTimePicker
                                displayStaticWrapperAs="desktop"
                                dateRangeIcon={
                                    <DateTimeLabel>
                                        <span>{t('general.Date')}</span>
                                        <Calendar size={20} />
                                    </DateTimeLabel>
                                }
                                timeIcon={
                                    <DateTimeLabel>
                                        <span>{t('general.Time')}</span>
                                        <ClockOutline size={20} />
                                    </DateTimeLabel>
                                }
                                value={endDate}
                                onChange={(value: Date) => setEndDate(value)}
                                ampm={false}
                                renderInput={() => null}
                            />
                        </Box>
                    </DateTimePickerContainer>
                </CalendarContainer>
            </LocalizationProvider>
            <QuickSelectorContainer>
                <Button onClick={selectYesterday} type="text">
                    {t('general.Yesterday')}
                </Button>
                <Button onClick={selectLastSevenDays} type="text">
                    {t('expressions.last_seven_days')}
                </Button>
                <Button onClick={selectLastThirtyDays} type="text">
                    {t('expressions.last_thirty_days')}
                </Button>
                <Button onClick={selectThisMonth} type="text">
                    {t('expressions.this_month')}
                </Button>
                <Button onClick={selectLastMonth} type="text">
                    {t('expressions.last_month')}
                </Button>
            </QuickSelectorContainer>
        </DateSelectorContainer>
    );
};
